/**
 * @file repOrderCard.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description What a rep's phone card says about one customer order
 *              (RepOrderCard). Cam, 28 Oct 2026: "the 'cards' are not looking
 *              right on mobile … overlapping text, awkward layout,
 *              not-intuitive tap areas triggering effects".
 *
 * The desktop row says where an order has got to with eight coloured icons
 * whose meaning is in their hover titles. A phone has no hover, so on the
 * card the same facts are said in words: one status ("Transport planned",
 * "Delivered", "Invoiced"…), whether it is priced, and how it gets there.
 * The words come from the same columns the icons read (utils/orderStatusIcons)
 * — nothing here decides a status the desktop does not.
 *
 * Pure: no React, no fetch, today passed in so the tests can pin it.
 *
 * 1.0.1 (2026-10-28, N6): perUnitText — what a phone line block's price is per
 * — is utils/unitPriceLabel's one rule ("per tonne", "per 25kg bag", "each",
 * "per bag", "(unit not set)"); it said "a tonne", and "each" for an unknown
 * unit too.
 */

import { redeliveryDeliveryStatus, redeliveryPillText } from './redelivery';
import { londonToday } from './transportLock';
import { isCustomerCollection } from './deliveryTerms';
import { includesPillText, addedToPillText } from './tonnageAdditions';
import { poStatusLabel } from './poStatus';
import { perUnitOf, priceFactsOf } from './unitPriceLabel';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOf = (d) => {
  const s = d == null ? '' : String(d).slice(0, 10);
  return DAY.test(s) ? s : null;
};
const text = (v) => String(v == null ? '' : v).trim();

/** Whole days from `from` to `to` (both 'YYYY-MM-DD'). */
function daysBetween(from, to) {
  const a = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  const b = Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10));
  return Math.round((b - a) / 86400000);
}

/**
 * The delivery date as a rep reads it: "Thu 29 Oct", with "Today" /
 * "Tomorrow" / "Yesterday" beside it when it is one of those, and the year
 * only when it is not this year.
 * @returns {{ text: string, when: string }}
 */
export function deliveryDateParts(date, today = londonToday()) {
  const d = dayOf(date);
  if (!d) return { text: 'No date', when: '' };
  const t = dayOf(today) || d;
  const diff = daysBetween(t, d);
  const when = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday' : '';
  const at = new Date(`${d}T12:00:00Z`);
  const weekday = at.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const dayMonth = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const year = d.slice(0, 4) === t.slice(0, 4) ? '' : ` ${d.slice(0, 4)}`;
  return { text: `${weekday} ${dayMonth}${year}`, when };
}

/**
 * Where the order has got to, in one phrase, and its tone
 * ('success' | 'info' | 'warning' | 'muted').
 *
 * Furthest stage first: invoiced, a draft invoice, a refused delivery waiting
 * to go again, the delivery status, then whether transport is planned.
 */
export function repOrderStatus(order = {}, today = londonToday()) {
  if (order.is_archived) return { label: 'Archived', tone: 'muted' };
  if (order.invoiced_status === 'Invoiced') return { label: 'Invoiced', tone: 'success' };
  if (order.draft_invoice_no) return { label: 'Invoice being priced', tone: 'info' };
  if (redeliveryDeliveryStatus(order.redelivery, today)) {
    return { label: redeliveryPillText(order.redelivery, today) || 'Held for redelivery', tone: 'warning' };
  }
  switch (order.delivery_status) {
    case 'Delivered': return { label: 'Delivered', tone: 'success' };
    case 'Partially Delivered': return { label: 'Part delivered', tone: 'warning' };
    case 'In-Transit': return { label: 'On its way', tone: 'info' };
    case 'Despatched': return { label: order.collected_date ? 'Collected' : 'Despatched', tone: 'info' };
    case 'Partially Despatched': return { label: 'Part despatched', tone: 'warning' };
    default: break;
  }
  if (isCustomerCollection(order.supplier_delivery_terms, order.excel_agri_delivery_terms)) {
    return { label: 'Customer collecting', tone: 'info' };
  }
  if (order.transport_status === 'Transport Planned') return { label: 'Transport planned', tone: 'info' };
  if (order.transport_status === 'Partially Planned') return { label: 'Part planned', tone: 'warning' };
  return { label: 'Not planned yet', tone: 'muted' };
}

/** "Not priced" / "Part priced", or null once every line has a price. */
export function repPriceState(order = {}) {
  if (order.is_priced === 'Priced') return null;
  if (order.is_priced === 'Partially Priced') return { label: 'Part priced', tone: 'warning' };
  return { label: 'Not priced', tone: 'warning' };
}

/** How it gets to the customer, when it is anything but our own delivery. */
export function repDeliveryRoute(order = {}) {
  const ours = order.excel_agri_delivery_terms;
  const theirs = order.supplier_delivery_terms;
  if (isCustomerCollection(theirs, ours)) return 'Customer collects';
  if (ours === 'Direct Delivery' || theirs === 'Direct Delivery') return 'Direct from the supplier';
  if (ours === 'Third-Party Delivery') {
    return text(order.haulier_name) ? `Haulier: ${text(order.haulier_name)}` : 'Third-party haulier';
  }
  return '';
}

/**
 * The few things worth flagging on the card, as plain words: a split, a
 * diversion, tonnage shared with another order. Redelivery and archived are
 * the status itself, so they are not repeated here.
 * @returns {Array<{ key: string, label: string }>}
 */
export function repOrderFlags(order = {}) {
  const flags = [];
  if (order.is_split) {
    flags.push({ key: 'split', label: order.parent_order_number ? `Split from ${order.parent_order_number}` : 'Split order' });
  } else if (order.has_splits) {
    flags.push({ key: 'split', label: 'Has split orders' });
  }
  if (Array.isArray(order.diversions) && order.diversions.length > 0) {
    flags.push({ key: 'diverted', label: 'Diverted' });
  }
  const adds = order.tonnage_additions || {};
  const added = addedToPillText(adds.added_to);
  if (added) flags.push({ key: 'added-to', label: added });
  const includes = includesPillText(adds.includes);
  if (includes) flags.push({ key: 'includes', label: includes });
  return flags;
}

/**
 * What was ordered, one entry per order line: its name and the line itself
 * (for the quantity in its own unit). Falls back to the row's item summary
 * (code, name, quantity) when the search sent no lines.
 * @returns {Array<{ key: string|number, code: string, name: string, quantity: number|null, line: object|null }>}
 */
export function repOrderItems(order = {}, fallback = []) {
  if (Array.isArray(order.lines) && order.lines.length > 0) {
    return order.lines.map((line) => ({
      key: line.id,
      code: text(line.item_code),
      name: text(line.item_name) || text(line.sale_description) || text(line.item_code) || 'Line',
      quantity: line.quantity == null ? null : Number(line.quantity),
      line
    }));
  }
  return (fallback || []).map((item) => ({
    key: item.key,
    code: text(item.code),
    name: text(item.name) || text(item.code) || 'Line',
    quantity: item.quantity == null ? null : Number(item.quantity),
    line: null
  }));
}

/**
 * A line's source in words: "PO-D10727 · Sent", "From our stock", "No PO
 * yet". "Not sent to the supplier yet" only when the PO says so — a status
 * of Sent or beyond already says it went.
 */
export function repLineSource(line = {}, po = null) {
  if (po) {
    const unsent = po.sent_to_contact === false ? ' · not sent to the supplier yet' : '';
    return `${po.purchase_order_number} · ${poStatusLabel(po.status)}${unsent}`;
  }
  if (line.is_stock) return 'From our stock';
  return 'No PO yet';
}

/** The card's one-line summary of suppliers: "MVFS - Lifton", "MVFS - Lifton + 1 more". */
export function suppliersText(suppliers = []) {
  const names = (suppliers || []).map((s) => text(s && s.name)).filter(Boolean);
  if (!names.length) return '';
  return names.length === 1 ? names[0] : `${names[0]} + ${names.length - 1} more`;
}

/**
 * What a line's price is per, after the figure and a space — the one rule
 * (utils/unitPriceLabel): "per tonne", "per 25kg bag", "each" (None),
 * "per bag" (milk powder), "(unit not set)" when nothing gives it.
 */
export function perUnitText(line = {}) {
  return perUnitOf(priceFactsOf(line || {})).phrase;
}
